<template>
  <div class="px-6 py-5">
    <div v-if="!messages.length" class="flex h-full flex-col items-center justify-center text-center">
      <div class="flex size-11 items-center justify-center rounded-2xl bg-surface-2 text-brand-700">
        <Sparkles class="size-5" />
      </div>
      <p class="mt-3 text-sm font-medium text-ink">{{ $t('chat-ui.empty.title') }}</p>
      <p class="mt-1 max-w-sm text-sm text-muted">{{ $t('chat-ui.empty.hint') }}</p>
      <div class="mt-4 flex max-w-md flex-wrap justify-center gap-2">
        <button
          v-for="s in suggestions"
          :key="s"
          type="button"
          class="rounded-full border border-line bg-surface px-3 py-1.5 text-xs text-muted transition-colors hover:border-brand-300 hover:text-ink"
          @click="emit('suggest', s)"
        >{{ s }}</button>
      </div>
    </div>
    <div v-else class="mx-auto w-full max-w-5xl space-y-5">
      <div
        v-for="(m, i) in messages"
        :key="i"
        class="group flex items-start gap-2.5"
        :class="m.role === 'user' ? 'justify-end' : 'justify-start'"
      >
        <!-- Assistant avatar -->
        <div
          v-if="m.role !== 'user'"
          class="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-2 text-brand-700"
        >
          <Bot class="size-4" />
        </div>

        <!-- Copy (user, left of bubble) -->
        <button
          v-if="m.role === 'user' && copyableText(m)"
          type="button"
          class="mt-1 shrink-0 self-center rounded-md p-1 text-faint opacity-0 transition hover:bg-surface-2 hover:text-ink group-hover:opacity-100"
          :title="copiedIdx === i ? 'Copied' : 'Copy'"
          @click="copyMessage(m, i)"
        >
          <component :is="copiedIdx === i ? Check : Copy" class="size-3.5" />
        </button>

        <!-- App/interactive views take the full column width; text stays at 85%. -->
        <div class="min-w-0" :class="hasApp(m) ? 'w-full' : 'max-w-[85%]'">
          <!-- User bubble -->
          <div
            v-if="m.role === 'user'"
            class="rounded-2xl rounded-br-sm bg-brand-600 px-3.5 py-2 text-sm text-white"
          >
            <div v-if="m.images?.length" class="mb-2 flex flex-wrap gap-2">
              <img
                v-for="(img, k) in m.images"
                :key="k"
                :src="img.dataUrl"
                alt="attachment"
                class="max-h-44 max-w-[12rem] rounded-lg border border-white/25 object-contain"
              />
            </div>
            <p v-if="typeof m.content === 'string'" class="whitespace-pre-wrap">{{ m.content }}</p>
          </div>

          <!-- Assistant: text renders as markdown, tool blocks are self-contained cards -->
          <div v-else class="space-y-2 text-sm text-ink">
            <MarkdownView v-if="typeof m.content === 'string'" :text="m.content" />
            <template v-else>
              <template v-for="(b, j) in m.content" :key="j">
                <ChatAppFrame v-if="b.type === 'ui_resource' && b.app" :app="b.app" />
                <component v-else :is="resolveBlock(b)" :block="b" />
              </template>
            </template>
          </div>
        </div>

        <!-- Copy (assistant, right of bubble) -->
        <button
          v-if="m.role !== 'user' && copyableText(m)"
          type="button"
          class="mt-1 shrink-0 self-center rounded-md p-1 text-faint opacity-0 transition hover:bg-surface-2 hover:text-ink group-hover:opacity-100"
          :title="copiedIdx === i ? 'Copied' : 'Copy response'"
          @click="copyMessage(m, i)"
        >
          <component :is="copiedIdx === i ? Check : Copy" class="size-3.5" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Sparkles, Bot, Copy, Check } from 'lucide-vue-next';
import TextBlock from './TextBlock.vue';
import MarkdownView from './MarkdownView.vue';
import ToolCallBubble from './ToolCallBubble.vue';
import ToolResultBubble from './ToolResultBubble.vue';
import ConfirmCard from './ConfirmCard.vue';
import ChatAppFrame from './ChatAppFrame.vue';
import type { ChatMessage, TraceBlock } from '../../stores/chat';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  messages: ChatMessage[];
}>();
const emit = defineEmits<{ suggest: [text: string] }>();

const { t } = useI18n();
// Empty-state starter prompts — one click sends them (wired to the chat's send).
const suggestions = computed(() => [
  t('chat-ui.empty.s1'), t('chat-ui.empty.s2'), t('chat-ui.empty.s3'),
  t('chat-ui.empty.s4'), t('chat-ui.empty.s5'), t('chat-ui.empty.s6'),
]);

const copiedIdx = ref<number | null>(null);

/** Plain text of a message (user string, or the assistant's text blocks). */
function copyableText(m: ChatMessage): string {
  if (typeof m.content === 'string') return m.content;
  return (m.content as TraceBlock[]).filter((b) => b.type === 'text' && b.text).map((b) => b.text).join('\n').trim();
}

async function copyMessage(m: ChatMessage, i: number): Promise<void> {
  const text = copyableText(m);
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    copiedIdx.value = i;
    setTimeout(() => { if (copiedIdx.value === i) copiedIdx.value = null; }, 1500);
  } catch { /* clipboard blocked (insecure context) — ignore */ }
}

function resolveBlock(b: TraceBlock) {
  if (b.type === 'tool_use') return ToolCallBubble;
  if (b.type === 'tool_result') return ToolResultBubble;
  if (b.type === 'confirm') return ConfirmCard;
  return TextBlock;
}

/** True if the assistant message carries an inline app/view (gets full width). */
function hasApp(m: ChatMessage): boolean {
  return Array.isArray(m.content) && (m.content as TraceBlock[]).some((b) => b.type === 'ui_resource');
}
</script>
